import { useState } from "react";

export interface SavedDashboardView {
  id: string;
  name: string;
  description?: string;
  stage: string;
  filters: string;
  createdAt: string;
}

const STORAGE_KEY = "lusca-search-dashboard-views";

interface UseSavedDashboardViewsReturn {
  views: SavedDashboardView[];
  saveView: (
    name: string,
    description: string,
    stage: string,
    filters: string
  ) => void;
  deleteView: (id: string) => void;
  renameView: (id: string, newName: string) => void;
}

export default function useSavedDashboardViews(): UseSavedDashboardViewsReturn {
  const [views, setViews] = useState<SavedDashboardView[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  });

  const saveToStorage = (updated: SavedDashboardView[]) => {
    setViews(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  };

  const isNameTaken = (name: string, excludeId?: string) =>
    views.some(
      (v) => v.name.toLowerCase() === name.toLowerCase() && v.id !== excludeId
    );

  const saveView = (
    name: string,
    description: string,
    stage: string,
    filters: string
  ): void => {
    if (isNameTaken(name)) {
      throw new Error("A saved view with this name already exists");
    }
    const newView: SavedDashboardView = {
      id: crypto.randomUUID(),
      name,
      description: description || undefined,
      stage,
      filters,
      createdAt: new Date().toISOString(),
    };
    saveToStorage([...views, newView]);
  };

  const deleteView = (id: string) => {
    saveToStorage(views.filter((v) => v.id !== id));
  };

  const renameView = (id: string, newName: string): void => {
    if (isNameTaken(newName, id)) {
      throw new Error("A saved view with this name already exists");
    }
    saveToStorage(
      views.map((v) => (v.id === id ? { ...v, name: newName } : v))
    );
  };

  return { views, saveView, deleteView, renameView };
}
